import React, { useState, useEffect, useRef } from "react";
import { X, MapPin, Video, Building2, Navigation } from "lucide-react";
import { useLocationSuggestions } from "../../hooks/useLocationSuggestions";

const locationTypes = [
	{ id: "physical", label: "In-person", icon: Navigation },
	{ id: "virtual", label: "Virtual", icon: Video },
	{ id: "hybrid", label: "Hybrid", icon: Building2 },
];

const LocationModal = ({ location, onChange, onClose }) => {
	const [locationInput, setLocationInput] = useState(location || "");
	const [activeType, setActiveType] = useState("physical");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [error, setError] = useState("");
	const suggestionsRef = useRef(null);
	const inputRef = useRef(null);

	const { suggestions, loading } = useLocationSuggestions(
		activeType === "virtual" ? "" : locationInput
	);

	// Show suggestions only when typing and active type allows it
	useEffect(() => {
		if (
			locationInput &&
			(activeType === "physical" || activeType === "hybrid")
		) {
			setShowSuggestions(true);
		} else {
			setShowSuggestions(false);
		}
	}, [locationInput, activeType]);

	// Close suggestions when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				suggestionsRef.current &&
				!suggestionsRef.current.contains(event.target) &&
				inputRef.current &&
				!inputRef.current.contains(event.target)
			) {
				setShowSuggestions(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleSelectLocation = (suggestion) => {
		setLocationInput(suggestion.description);
		onChange(suggestion.description);
		setShowSuggestions(false);
		setError("");
	};

	const validateAndSubmit = () => {
		if (!locationInput.trim()) {
			setError("Location is required");
			return;
		}
		onChange(locationInput);
		onClose();
	};

	return (
		<>
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={onClose}
			/>

			<div className="fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl">
				<div>
					<div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							Add location
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
						>
							<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
						</button>
					</div>

					<div className="p-4 space-y-4">
						<div className="grid grid-cols-3 gap-2">
							{locationTypes.map(({ id, label, icon: Icon }) => (
								<button
									key={id}
									type="button"
									onClick={() => {
										setActiveType(id);
										setLocationInput("");
										setError("");
									}}
									className={`p-3 rounded-lg flex flex-col items-center gap-2 text-sm transition-colors
                    ${
											activeType === id
												? "bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
												: "hover:bg-gray-100/50 dark:hover:bg-gray-800/50 text-gray-600 dark:text-gray-300"
										}`}
								>
									<Icon
										className={`w-5 h-5 ${
											activeType === id
												? "text-blue-500 dark:text-blue-400"
												: "text-gray-500 dark:text-gray-400"
										}`}
									/>
									{label}
								</button>
							))}
						</div>

						<div className="space-y-2 relative">
							<input
								ref={inputRef}
								type="text"
								value={locationInput}
								onChange={(e) => setLocationInput(e.target.value)}
								placeholder={
									activeType === "virtual"
										? "Enter meeting link"
										: "Search for a location"
								}
								className={`w-full px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg 
                  text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400
                  focus:outline-none focus:ring-2 
                  ${error ? "ring-2 ring-red-500" : "focus:ring-blue-500"}`}
							/>

							{/* Suggestions dropdown */}
							{showSuggestions &&
								suggestions.length > 0 &&
								(activeType === "physical" || activeType === "hybrid") && (
									<div
										ref={suggestionsRef}
										className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-48 overflow-auto scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600"
									>
										{loading ? (
											<p className="p-4 text-sm text-gray-500 dark:text-gray-400">
												Loading...
											</p>
										) : (
											suggestions.map((suggestion) => (
												<button
													key={suggestion.placeId}
													type="button"
													onClick={() => handleSelectLocation(suggestion)}
													className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
												>
													<MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
													<div className="flex flex-col">
														<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
															{suggestion.mainText}
														</span>
														<span className="text-xs text-gray-500 dark:text-gray-400">
															{suggestion.secondaryText}
														</span>
													</div>
												</button>
											))
										)}
									</div>
								)}

							{error && <p className="text-sm text-red-500 mt-1">{error}</p>}
						</div>
					</div>

					<div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-800">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={validateAndSubmit}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
						>
							Save
						</button>
					</div>
				</div>
			</div>
		</>
	);
};

export default LocationModal;
